// src/components/dashboard/UpcomingSwitches.jsx

import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  Button,
  Stack,
  Avatar,
} from "@mui/material";
import {
  SwapHoriz,
  NotificationsActive,
  Warning,
  ArrowForward,
  Business,
  CalendarToday,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useCollection } from "../../hooks/useCollection";

const UpcomingSwitches = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: calendarEvents } = useCollection("switchCalendar");

  const upcomingSwitches = useMemo(() => {
    if (!calendarEvents) return { urgent: [], upcoming: [] };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const active = calendarEvents.filter(
      (e) => e.isActive !== false && !e.completado
    );

    const urgent = [];
    const upcoming = [];

    active.forEach((event) => {
      const aniversario = new Date(event.fechaAniversario);
      aniversario.setHours(0, 0, 0, 0);
      const daysUntil = Math.ceil((aniversario - today) / (1000 * 60 * 60 * 24));

      if (daysUntil <= 30) {
        urgent.push({ ...event, daysUntil, urgency: daysUntil <= 0 ? "overdue" : "urgent" });
      } else if (daysUntil <= 60) {
        upcoming.push({ ...event, daysUntil, urgency: "warning" });
      }
    });

    // Ordenar por días restantes (más urgentes primero)
    urgent.sort((a, b) => a.daysUntil - b.daysUntil);
    upcoming.sort((a, b) => a.daysUntil - b.daysUntil);

    return { urgent, upcoming };
  }, [calendarEvents]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getUrgencyColor = (urgency, daysUntil) => {
    if (urgency === "overdue") return theme.palette.error;
    if (urgency === "urgent") return theme.palette.warning;
    return theme.palette.info;
  };

  const getUrgencyLabel = (urgency, daysUntil) => {
    if (urgency === "overdue") return `Vencido hace ${Math.abs(daysUntil)} días`;
    if (urgency === "urgent") return `${daysUntil} días`;
    return `${daysUntil} días`;
  };

  const totalSwitches = upcomingSwitches.urgent.length + upcomingSwitches.upcoming.length;

  // Si no hay ningún switch próximo o urgente, no mostrar el componente
  if (totalSwitches === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        height: "100%",
        borderTop: 4,
        borderColor: theme.palette.warning.main,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[8],
          transform: { xs: "none", sm: "translateY(-4px)" },
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.mode === "dark"
                  ? "rgba(255, 152, 0, 0.15)"
                  : "rgba(255, 152, 0, 0.1)",
                color: theme.palette.warning.main,
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
              }}
            >
              <SwapHoriz sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" } }}
              >
                Cambios de Comercializadora
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              >
                {upcomingSwitches.urgent.length} urgentes · {upcomingSwitches.upcoming.length} próximos
              </Typography>
            </Box>
          </Box>

          <Button
            size="small"
            endIcon={<ArrowForward />}
            onClick={() => navigate("/switch-calendar")}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              color: theme.palette.warning.main,
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
              "&:hover": {
                backgroundColor: theme.palette.mode === "dark"
                  ? "rgba(255, 152, 0, 0.1)"
                  : "rgba(255, 152, 0, 0.05)",
              },
            }}
          >
            Ver calendario
          </Button>
        </Box>

        {/* Urgentes */}
        {upcomingSwitches.urgent.length > 0 && (
          <Box sx={{ mb: upcomingSwitches.upcoming.length > 0 ? 2 : 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <Warning sx={{ fontSize: 16, color: theme.palette.error.main }} />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ color: theme.palette.error.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                Urgentes
              </Typography>
            </Box>

            <List dense disablePadding>
              {upcomingSwitches.urgent.slice(0, 3).map((switchItem) => {
                const urgencyColor = getUrgencyColor(switchItem.urgency, switchItem.daysUntil);
                return (
                  <ListItem
                    key={switchItem.id}
                    sx={{
                      mb: 0.5,
                      borderRadius: "12px",
                      borderLeft: `3px solid ${urgencyColor.main}`,
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <NotificationsActive sx={{ fontSize: 18, color: urgencyColor.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                        >
                          {switchItem.switchClientName || "Sin nombre"}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.3 }}>
                          <Business sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                          >
                            {switchItem.supplierName || "—"}
                          </Typography>
                          <CalendarToday sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 0.5 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                          >
                            {formatDate(switchItem.fechaAniversario)}
                          </Typography>
                        </Stack>
                      }
                      sx={{ my: 0 }}
                    />
                    <Chip
                      label={getUrgencyLabel(switchItem.urgency, switchItem.daysUntil)}
                      size="small"
                      sx={{
                        backgroundColor: urgencyColor.main,
                        color: "white",
                        fontWeight: 700,
                        fontSize: { xs: "0.6rem", sm: "0.7rem" },
                        height: 24,
                        minWidth: 60,
                      }}
                    />
                  </ListItem>
                );
              })}
              {upcomingSwitches.urgent.length > 3 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 0.5, fontSize: "0.7rem" }}
                >
                  +{upcomingSwitches.urgent.length - 3} más urgentes
                </Typography>
              )}
            </List>
          </Box>
        )}

        {/* Próximos */}
        {upcomingSwitches.upcoming.length > 0 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <NotificationsActive sx={{ fontSize: 16, color: theme.palette.info.main }} />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ color: theme.palette.info.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
              >
                Próximos
              </Typography>
            </Box>

            <List dense disablePadding>
              {upcomingSwitches.upcoming.slice(0, 2).map((switchItem) => {
                const urgencyColor = getUrgencyColor(switchItem.urgency, switchItem.daysUntil);
                return (
                  <ListItem
                    key={switchItem.id}
                    sx={{
                      mb: 0.5,
                      borderRadius: "12px",
                      borderLeft: `3px solid ${urgencyColor.main}`,
                      backgroundColor: theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(0,0,0,0.01)",
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.03)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CalendarToday sx={{ fontSize: 18, color: urgencyColor.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                        >
                          {switchItem.switchClientName || "Sin nombre"}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.3 }}>
                          <Business sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                          >
                            {switchItem.supplierName || "—"}
                          </Typography>
                          <CalendarToday sx={{ fontSize: 12, color: theme.palette.text.secondary, ml: 0.5 }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                          >
                            {formatDate(switchItem.fechaAniversario)}
                          </Typography>
                        </Stack>
                      }
                      sx={{ my: 0 }}
                    />
                    <Chip
                      label={`${switchItem.daysUntil} días`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: urgencyColor.main,
                        color: urgencyColor.main,
                        fontWeight: 600,
                        fontSize: { xs: "0.6rem", sm: "0.7rem" },
                        height: 24,
                        minWidth: 60,
                      }}
                    />
                  </ListItem>
                );
              })}
              {upcomingSwitches.upcoming.length > 2 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 0.5, fontSize: "0.7rem" }}
                >
                  +{upcomingSwitches.upcoming.length - 2} más próximos
                </Typography>
              )}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingSwitches;